import {
  EuiBasicTable,
  EuiBasicTableColumn,
  EuiButtonEmpty,
  EuiButtonIcon,
  EuiContextMenuItem,
  EuiContextMenuPanel,
  EuiFlexGroup,
  EuiFlexItem,
  EuiPanel,
  EuiPopover,
  EuiScreenReaderOnly,
} from "@inscopix/ideas-eui";
import { ReactNode, useState } from "react";
import { createToolVersionDraft } from "components/ToolEditor/api/createToolVersionDraft";
import { useRouteMapContext } from "providers/RouteMapProvider/RouteMapProvider";
import { formatDate } from "utils/formatDate";
import { useTenantContext } from "providers/TenantProvider/TenantProvider";
import { useQueryClient } from "@tanstack/react-query";
import { ToolDetails } from "pages/tool/PageToolQuery";
import { addUtilityToastFailure } from "utils/addUtilityToastFailure";
import { isNonNullish } from "utils/isNonNullish";
import { ToolStatus } from "types/constants";
import { SetNonNullable } from "type-fest";
import { captureException } from "@sentry/react";
import { isToolSpecV2 } from "types/json-schemas/type_guards";
import { ModalDeleteToolVersion } from "components/ModalDeleteToolVersion/ModalDeleteToolVersion";
import { ToolPanelVersionDetail } from "./ToolPanelVersionDetail";
import { DRS_FILE_ACTION_ICONS } from "types/Icons";
import { downloadToolSpec } from "components/ToolEditor/api/downloadToolSpec";
import { Tool } from "graphql/_Types";

export type ToolVersion = ToolDetails["versions"][number] & {
  toolId: Tool["id"];
};

const ToolVersionActions = (toolVersion: ToolVersion) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isDownloadSpecLoading, setIsDownloadSpecLoading] = useState(false);

  const handleDownloadToolSpec = async () => {
    setIsDownloadSpecLoading(true);
    try {
      await downloadToolSpec(toolVersion.id);
    } catch (err) {
      addUtilityToastFailure("Failed to download tool specification");
    }
    setIsDownloadSpecLoading(false);
  };

  return (
    <>
      {isDeleteModalVisible && (
        <ModalDeleteToolVersion
          toolId={toolVersion.toolId}
          toolVersionId={toolVersion.id}
          toolVersionVersion={toolVersion.version}
          onClose={() => setIsDeleteModalVisible(false)}
        />
      )}

      <EuiPopover
        data-test-subj="tool-version-actions-popover"
        button={
          <EuiButtonIcon
            iconType="gear"
            onClick={() => setIsPopoverOpen((isOpen) => !isOpen)}
          />
        }
        isOpen={isPopoverOpen}
        closePopover={() => setIsPopoverOpen(false)}
        panelPaddingSize="none"
        anchorPosition="rightCenter"
      >
        <EuiContextMenuPanel
          size="s"
          items={[
            <EuiContextMenuItem
              disabled={isDownloadSpecLoading}
              key="download-spec"
              icon={DRS_FILE_ACTION_ICONS["download"]}
              onClick={() => void handleDownloadToolSpec()}
            >
              Download specification
            </EuiContextMenuItem>,
            <EuiContextMenuItem
              disabled={toolVersion.status === ToolStatus.DEPRECATED}
              key="deprecate"
              icon="trash"
              onClick={() => {
                setIsPopoverOpen(false);
                setIsDeleteModalVisible(true);
              }}
            >
              Deprecate
            </EuiContextMenuItem>,
          ]}
        />
      </EuiPopover>
    </>
  );
};

const columns: Array<EuiBasicTableColumn<ToolVersion>> = [
  {
    field: "version",
    name: "Version",
    sortable: true,
    truncateText: false,
  },
  {
    field: "created",
    dataType: "date",
    name: "Date Created",
    render: (created: ToolVersion["created"]) => formatDate(created),
  },
  {
    field: "status",
    name: "Status",
    render: (status: ToolVersion["status"]) => {
      switch (status) {
        case ToolStatus["DRAFT"]:
          return "Draft";
        case ToolStatus["PUBLISHED"]:
          return "Published";
        case ToolStatus["DEPRECATED"]:
          return "Deprecated";
        default:
          return "Unknown";
      }
    },
  },
];

interface ToolPanelVersionsProps {
  tool: SetNonNullable<ToolDetails, "latest_tool_version">;
}

/*
 * Panel displaying the versions of a tool, with button to create a new version
 */
export const ToolPanelVersions = ({ tool }: ToolPanelVersionsProps) => {
  const [isCreatingVersion, setIsCreatingVersion] = useState(false);
  const toolVersions = tool.versions.map((version) => ({
    ...version,
    toolId: tool.id,
  }));
  const viewOnlyMode = tool.project === null;
  const queryClient = useQueryClient();
  const currentTenantKey = useTenantContext((s) => s.currentTenant.key);
  const routeMap = useRouteMapContext((s) => s.routeMap);

  // get a list of all versions that are not in draft status
  const publishedVersions = toolVersions.filter(
    (v) => v.status !== ToolStatus["DRAFT"],
  );

  const latestVersion = tool.latest_tool_version;

  /**
   * Expanding rows
   */
  const [itemIdToExpandedRowMap, setItemIdToExpandedRowMap] = useState<
    Record<string, ReactNode>
  >({});

  const toggleDetails = (version: ToolVersion) => {
    const itemIdToExpandedRowMapValues = { ...itemIdToExpandedRowMap };

    if (isNonNullish(itemIdToExpandedRowMapValues[version.id])) {
      delete itemIdToExpandedRowMapValues[version.id];
    } else {
      itemIdToExpandedRowMapValues[version.id] = (
        <ToolPanelVersionDetail toolVersion={version} />
      );
    }
    setItemIdToExpandedRowMap(itemIdToExpandedRowMapValues);
  };

  const columnsWithExpandingRowToggle: Array<EuiBasicTableColumn<ToolVersion>> =
    [
      ...columns,
      ...(tool.project !== null
        ? [
            {
              name: "Actions",
              actions: [
                {
                  name: "Actions",
                  description: "Tool version actions",
                  render: ToolVersionActions,
                },
              ],
            } as EuiBasicTableColumn<ToolVersion>,
          ]
        : []),
      {
        align: "right",
        width: "40px",
        isExpander: true,
        name: (
          <EuiScreenReaderOnly>
            <span>View parameters and outputs</span>
          </EuiScreenReaderOnly>
        ),
        render: (version: ToolVersion) => {
          const itemIdToExpandedRowMapValues = { ...itemIdToExpandedRowMap };
          if (isToolSpecV2(version.tool_spec)) {
            return null;
          }

          return (
            <EuiButtonIcon
              onClick={() => toggleDetails(version)}
              aria-label={
                isNonNullish(itemIdToExpandedRowMapValues[version.id])
                  ? "Collapse"
                  : "Expand"
              }
              iconType={
                isNonNullish(itemIdToExpandedRowMapValues[version.id])
                  ? "arrowDown"
                  : "arrowRight"
              }
            />
          );
        },
      },
    ];

  const handleCreateNewVersion = async () => {
    setIsCreatingVersion(true);
    try {
      const res = await createToolVersionDraft({ tool: tool.id });
      await queryClient.invalidateQueries(["tool", tool.id]);
      // navigate to the new version's editor
      routeMap.TOOL_EDITOR.dynamicPath({
        tenantKey: currentTenantKey,
        toolId: tool.id,
        toolVersionId: res.data.tool_version.id,
      }).navigateTo();
    } catch (error) {
      addUtilityToastFailure("Failed to create new tool version");
      captureException(error);
    }
    setIsCreatingVersion(false);
  };

  return (
    <EuiPanel paddingSize="l" hasShadow={false} hasBorder={true}>
      <EuiFlexGroup direction="column" gutterSize="m">
        <EuiFlexItem grow={false}>
          <EuiFlexGroup>
            <EuiFlexItem grow={true}>
              <strong>Latest Version</strong>
            </EuiFlexItem>
            {viewOnlyMode !== true && (
              <EuiFlexItem grow={false}>
                {
                  // if the most recent version is not a draft, show "New Version" button
                  latestVersion !== null &&
                  latestVersion.status !== ToolStatus["DRAFT"] ? (
                    <EuiButtonEmpty
                      size="s"
                      iconType="plusInCircle"
                      isLoading={isCreatingVersion}
                      onClick={() => {
                        void handleCreateNewVersion();
                      }}
                    >
                      New Version
                    </EuiButtonEmpty>
                  ) : (
                    // if the most recent version is a draft, show "Edit Draft" button
                    <EuiButtonEmpty
                      size="s"
                      iconType="pencil"
                      onClick={() => {
                        routeMap.TOOL_EDITOR.dynamicPath({
                          tenantKey: currentTenantKey,
                          toolId: tool.id,
                          toolVersionId: latestVersion.id,
                        }).navigateTo();
                      }}
                    >
                      Edit Draft
                    </EuiButtonEmpty>
                  )
                }
              </EuiFlexItem>
            )}
          </EuiFlexGroup>
        </EuiFlexItem>
        {publishedVersions.length > 0 && (
          <EuiFlexItem grow={false}>
            <EuiBasicTable
              tableCaption="Latest tool version"
              items={[publishedVersions[0]]}
              itemId="id"
              columns={columnsWithExpandingRowToggle}
              itemIdToExpandedRowMap={itemIdToExpandedRowMap}
              isExpandable={true}
              responsive={false}
            />
          </EuiFlexItem>
        )}
        {publishedVersions.length > 1 && (
          <>
            <EuiFlexItem grow={false}>
              <strong>Previous Versions</strong>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiBasicTable
                tableCaption="Previous tool versions"
                items={publishedVersions.slice(1)}
                itemId="id"
                columns={columnsWithExpandingRowToggle}
                itemIdToExpandedRowMap={itemIdToExpandedRowMap}
                isExpandable={true}
                responsive={false}
              />
            </EuiFlexItem>
          </>
        )}
      </EuiFlexGroup>
    </EuiPanel>
  );
};
